/**
 * ChildOverviewSection — one card per kid: what they are doing, how their week
 * is going, and where their streak stands.
 *
 * There was no single place to answer "how is Bella doing this week". The
 * information existed, spread across four routes a parent had to visit in turn.
 *
 * Composed client-side from endpoints that all already exist rather than a new
 * rollup route: the board feed (counts), /api/streaks/:id and
 * /api/leaderboard/weekly. For a four-person household that is a handful of
 * cached requests, and it avoids a bespoke aggregate whose only consumer would
 * be this panel.
 *
 * #159 (redesign steg 9a, Stell_Familie): glasskort med InitialAvatar (D2),
 * «Navn · N år» fra `birth_date`, ukepoeng og streak på én linje, og tre
 * telleruter (ledige · i gang · venter). Navnet lenker til barnets lommebok,
 * som viser saldoen; saldoen står derfor ikke lenger på kortet. «Juster»
 * åpner justeringen inne i kortet (AdjustPointsPanel) i stedet for et ark, og
 * bare ett kort justerer om gangen. Allergier og kosthold står på kortet
 * (DietaryPills), fordi de er sikkerhetskritiske.
 *
 * Utløpt adminopplåsing (MAPPING.md §5 punkt 9): GET /api/members svarer 403
 * når de ti minuttene er ute. Da viser seksjonen AdminScopeLocked, ikke «Ingen
 * barn registrert ennå», som var feil og fikk familien til å se tom ut.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { MemberAdminRow } from '@stell/shared';
import { Button, ErrorState, Icon, LoadingState, Pill } from '../../components/primitives';
import { GLASS_FILL } from '../../components/primitives/Card';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import { formatPoints } from '../../i18n/formatters';
import { AdminScopeLocked } from '../admin/AdminScopeLocked';
import { DietaryPills } from '../admin/DietaryPills';
import { isAdminScopeError, useAdminMembers } from '../admin/useMembers';
import { useBoard } from '../chores/useChoresQuery';
import { AdjustPointsPanel } from './AdjustPointsPanel';
import { ageInYears } from './age';

interface Totals {
  streak: number;
  weekPoints: number;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const r = await globalThis.fetch(url, { credentials: 'same-origin' });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Streak + this week's points for one member.
 *
 * Every leg degrades to 0 rather than throwing: a card showing "0 p" when the
 * streak endpoint hiccups is a smaller problem than the whole panel erroring
 * out, and the numbers refresh on the next WS invalidation anyway.
 */
function useChildTotals(memberId: string) {
  return useQuery<Totals>({
    queryKey: ['child-overview', memberId],
    queryFn: async () => {
      const [streak, board] = await Promise.all([
        fetchJson<{ current_streak?: number }>(`/api/streaks/${memberId}`),
        fetchJson<{ rows?: Array<{ member_id: string; points: number }> }>(
          '/api/leaderboard/weekly',
        ),
      ]);
      const week = board?.rows?.find((r) => r.member_id === memberId)?.points ?? 0;
      return {
        streak: streak?.current_streak ?? 0,
        weekPoints: week,
      };
    },
    staleTime: 30_000,
  });
}

interface StatProps {
  label: string;
  value: number;
  /** Amber når noe venter på en voksen; tallet og ordet står der uansett. */
  warn?: boolean;
}

function Stat({ label, value, warn = false }: StatProps): ReactElement {
  return (
    <div className={`rounded-md p-2 text-center ${warn ? 'bg-warn-bg text-warn' : 'bg-inset text-ink'}`}>
      <div className="tnum text-lg font-extrabold">{value}</div>
      <div className={`text-label ${warn ? '' : 'text-ink-2'}`}>{label}</div>
    </div>
  );
}

interface ChildCardProps {
  member: MemberAdminRow;
  open: number;
  active: number;
  awaiting: number;
  adjusting: boolean;
  onAdjust: () => void;
  onCloseAdjust: () => void;
}

function ChildCard({
  member,
  open,
  active,
  awaiting,
  adjusting,
  onAdjust,
  onCloseAdjust,
}: ChildCardProps): ReactElement {
  const { t } = useTranslation();
  const totals = useChildTotals(member.id);
  const d = totals.data;
  const age = ageInYears(member.birth_date, new Date());
  const name = member.short_name;

  // Fokus tilbake til Juster når justeringen lukkes (lagret eller avbrutt).
  const adjustRef = useRef<HTMLButtonElement>(null);
  const wasAdjusting = useRef(adjusting);
  useEffect(() => {
    if (wasAdjusting.current && !adjusting) adjustRef.current?.focus();
    wasAdjusting.current = adjusting;
  }, [adjusting]);

  return (
    <article
      data-testid={`child-card-${member.id}`}
      className={[
        GLASS_FILL,
        'flex flex-col gap-3 rounded-lg border p-3.5 shadow-md',
        // Skissen gir kortet som justerer en tydeligere kant.
        adjusting ? 'border-secondary-line' : 'border-card-line',
      ].join(' ')}
    >
      <div className="flex items-center gap-3">
        <Link
          to={`/${member.id}/poeng`}
          data-testid={`child-wallet-${member.id}`}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md text-ink"
        >
          <InitialAvatar name={name} colorSlot={asColorSlot(member.color_token)} size={44} decorative />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-extrabold">
              {age === null ? name : t('admin.overview.nameAge', { name, age })}
            </span>
            {/* Brytes mellom ukepoeng og streak, ikke inne i dem: på telefon
                tar Juster-knappen plassen, og en avkortet streak er borte. */}
            <span className="block text-caption text-ink-2">
              {d ? (
                <>
                  <span className="whitespace-nowrap">
                    {t('admin.overview.weekPoints', { points: formatPoints(d.weekPoints) })} ·
                  </span>{' '}
                  <span className="whitespace-nowrap">{t('admin.overview.streakRun', { count: d.streak })}</span>
                </>
              ) : (
                t('admin.console.loading')
              )}
            </span>
          </span>
          <span className="sr-only">{t('admin.overview.viewWallet')}</span>
          <Icon.ChevR size={16} className="shrink-0 text-ink-2" />
        </Link>
        {adjusting ? (
          <Pill variant="lime">{t('admin.overview.adjusting')}</Pill>
        ) : (
          <Button
            ref={adjustRef}
            variant="secondary"
            size={44}
            onClick={onAdjust}
            aria-label={t('admin.adjust.title', { name })}
            data-testid={`child-adjust-${member.id}`}
          >
            {t('admin.overview.adjust')}
          </Button>
        )}
      </div>

      <DietaryPills dietary={member.dietary} data-testid={`child-dietary-${member.id}`} />

      {adjusting ? (
        <AdjustPointsPanel member={member} onClose={onCloseAdjust} />
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <Stat label={t('admin.overview.openLabel')} value={open} />
          <Stat label={t('admin.overview.activeLabel')} value={active} />
          <Stat label={t('admin.overview.awaitingLabel')} value={awaiting} warn={awaiting > 0} />
        </div>
      )}
    </article>
  );
}

export function ChildOverviewSection(): ReactElement {
  const { t } = useTranslation();
  const members = useAdminMembers();
  const board = useBoard();
  const [adjustFor, setAdjustFor] = useState<string | null>(null);

  const kids = useMemo(
    () => (members.data ?? []).filter((m) => m.role !== 'admin' && !m.archived_at),
    [members.data],
  );

  /** Per-member counts derived from the one board feed the app already loads. */
  const counts = useMemo(() => {
    const rows = board.data ?? [];
    const out = new Map<string, { open: number; active: number; awaiting: number }>();
    for (const k of kids) out.set(k.id, { open: 0, active: 0, awaiting: 0 });
    for (const r of rows) {
      if (r.status === 'available') {
        // An unrestricted card is claimable by everyone, so it counts for every
        // kid — that is what makes it "open to them".
        const eligible = (r.assignee_ids ?? []).length > 0 ? r.assignee_ids! : kids.map((k) => k.id);
        for (const id of eligible) {
          const c = out.get(id);
          if (c) c.open += 1;
        }
        continue;
      }
      if (!r.claimed_by) continue;
      const c = out.get(r.claimed_by);
      if (!c) continue;
      if (r.status === 'claimed') c.active += 1;
      else if (r.status === 'awaiting_approval') c.awaiting += 1;
    }
    return out;
  }, [board.data, kids]);

  return (
    <section
      aria-label={t('admin.overview.section_label')}
      className="flex flex-col gap-3"
      data-testid="child-overview-section"
    >
      {members.isLoading ? (
        <LoadingState lines={2} />
      ) : members.isError && isAdminScopeError(members.error) ? (
        // Sjekkes før dataene: en mislykket ny henting beholder de gamle radene,
        // men de kan ikke endres før adminen har låst opp igjen.
        <AdminScopeLocked testIdPrefix="child-overview" message={t('admin.overview.scope_locked')} />
      ) : members.isError && !members.data ? (
        // En mislykket ny henting med rader i hurtigbufferen beholder kortene,
        // så et halvutfylt justeringspanel ikke forsvinner.
        <ErrorState
          variant="inline"
          heading={t('admin.overview.load_error')}
          onRetry={() => void members.refetch()}
        />
      ) : kids.length === 0 ? (
        <p className="text-ink-2 text-sm" data-testid="child-overview-empty">
          {t('admin.overview.empty')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {kids.map((m) => {
            const c = counts.get(m.id) ?? { open: 0, active: 0, awaiting: 0 };
            return (
              <ChildCard
                key={m.id}
                member={m}
                open={c.open}
                active={c.active}
                awaiting={c.awaiting}
                adjusting={adjustFor === m.id}
                onAdjust={() => setAdjustFor(m.id)}
                // Bare hvis det fortsatt er dette kortet som justerer: en
                // lagring som blir ferdig etter at forelderen har åpnet et
                // annet kort, skal ikke lukke det kortet.
                onCloseAdjust={() => setAdjustFor((cur) => (cur === m.id ? null : cur))}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
